import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Switch, message } from 'antd';
import { getAppSettingsApi, updateAppSettingsApi } from '../../services/api';
import CampaignManager from './CampaignManager';

const { Option } = Select;

const INITIAL = {
  store: {
    name:     'Kidodom',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    language: 'English',
  },
  shipping: {
    freeOver:  '499',
    standard:  '3–5 days',
    express:   '1–2 days',
    returnWin: '7 days',
  },
  notifications: {
    lowStock:    true,
    orderEmails: true,
    reviewAlert: true,
    weeklyReport:true,
  },
  payments: {
    online: true,
    cod:    false,
  },
  mobileTabs: {
    contest:       true,
    parenting:     true,
    subscriptions: true,
  },
  loginOptions: {
    otp: true,
  },
  support: {
    email:      'support@kidodom.com',
    emailNote:  'Response within 24 hours',
    phone:      '+91 800 Kidodom',
    phoneNote:  'Mon–Sat, 9am – 6pm IST',
  },
  appUpdate: {
    enabled:            false,
    minVersionAndroid:  '',
    minVersionIos:      '',
    message:            '',
  },
  features: {
    tryOnEnabled: false,
  },
};

export default function Settings() {
  const [cfg, setCfg]       = useState(INITIAL);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAppSettingsApi()
      .then(({ data }) => {
        const s = data?.data;
        if (!s) return;
        setCfg(prev => ({
          ...prev,
          payments:   { online: s.onlinePaymentEnabled, cod: s.codEnabled },
          mobileTabs: {
            contest:       s.contestTabVisible,
            parenting:     s.parentingTabVisible,
            subscriptions: s.subscriptionsEnabled,
          },
          loginOptions: {
            otp: s.otpLoginEnabled,
          },
          support: {
            email:     s.supportEmail,
            emailNote: s.supportEmailNote,
            phone:     s.supportPhone,
            phoneNote: s.supportPhoneNote,
          },
          appUpdate: {
            enabled:    !!s.forceUpdateEnabled,
            // Falls back to the old shared field if a platform-specific one
            // was never set yet — same fallback mobile itself uses, so what
            // shows here always matches what's actually being enforced.
            minVersionAndroid: s.minSupportedVersionAndroid || s.minSupportedVersion || '',
            minVersionIos:     s.minSupportedVersionIos     || s.minSupportedVersion || '',
            message:    s.forceUpdateMessage || '',
          },
          features: {
            tryOnEnabled: !!s.tryOnEnabled,
          },
        }));
      })
      .catch(() => message.error('Failed to load app settings'));
  }, []);

  const set = (section, key, val) =>
    setCfg(prev => ({ ...prev, [section]: { ...prev[section], [key]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAppSettingsApi({
        codEnabled:           cfg.payments.cod,
        onlinePaymentEnabled: cfg.payments.online,
        contestTabVisible:    cfg.mobileTabs.contest,
        parentingTabVisible:  cfg.mobileTabs.parenting,
        subscriptionsEnabled: cfg.mobileTabs.subscriptions,
        otpLoginEnabled:      cfg.loginOptions.otp,
        supportEmail:         cfg.support.email,
        supportEmailNote:     cfg.support.emailNote,
        supportPhone:         cfg.support.phone,
        supportPhoneNote:     cfg.support.phoneNote,
        forceUpdateEnabled:        cfg.appUpdate.enabled,
        minSupportedVersionAndroid: cfg.appUpdate.minVersionAndroid || null,
        minSupportedVersionIos:     cfg.appUpdate.minVersionIos || null,
        forceUpdateMessage:        cfg.appUpdate.message || null,
        tryOnEnabled:              cfg.features.tryOnEnabled,
      });
      message.success('Settings saved successfully');
    } catch {
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.05)',
  };
  const labelStyle = { fontSize: 13, color: '#888', minWidth: 160 };
  const card = { background: '#fff', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', padding: 20 };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-sub">Manage your store configuration</div>
        </div>
        <Button
          type="primary"
          loading={saving}
          onClick={handleSave}
          style={{ background: '#534ab7', border: 'none', borderRadius: 8 }}
        >
          Save Changes
        </Button>
      </div>

      <div className="settings-columns">

        {/* ── Store Information ── */}
        <div className="settings-card" style={card}>
          <div className="section-title">Store Information</div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Store Name</span>
            <Input
              value={cfg.store.name}
              onChange={e => set('store', 'name', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Currency</span>
            <Select value={cfg.store.currency} onChange={v => set('store', 'currency', v)} style={{ width: 200 }}>
              <Option value="INR">INR (₹)</Option>
              <Option value="USD">USD ($)</Option>
              <Option value="EUR">EUR (€)</Option>
              <Option value="GBP">GBP (£)</Option>
            </Select>
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Timezone</span>
            <Select value={cfg.store.timezone} onChange={v => set('store', 'timezone', v)} style={{ width: 200 }}>
              <Option value="Asia/Kolkata">Asia/Kolkata (IST)</Option>
              <Option value="UTC">UTC</Option>
              <Option value="America/New_York">America/New_York</Option>
              <Option value="Europe/London">Europe/London</Option>
            </Select>
          </div>

          <div style={{ ...fieldStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Language</span>
            <Select value={cfg.store.language} onChange={v => set('store', 'language', v)} style={{ width: 200 }}>
              <Option value="English">English</Option>
              <Option value="Hindi">Hindi</Option>
              <Option value="Tamil">Tamil</Option>
            </Select>
          </div>
        </div>

        {/* ── Shipping & Returns ── */}
        <div className="settings-card" style={card}>
          <div className="section-title">Shipping &amp; Returns</div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Free shipping over (₹)</span>
            <Input
              value={cfg.shipping.freeOver}
              onChange={e => set('shipping', 'freeOver', e.target.value)}
              style={{ width: 200 }}
              prefix="₹"
              type="number"
              min={0}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Standard delivery</span>
            <Input
              value={cfg.shipping.standard}
              onChange={e => set('shipping', 'standard', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Express delivery</span>
            <Input
              value={cfg.shipping.express}
              onChange={e => set('shipping', 'express', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={{ ...fieldStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Return window</span>
            <Input
              value={cfg.shipping.returnWin}
              onChange={e => set('shipping', 'returnWin', e.target.value)}
              style={{ width: 200 }}
            />
          </div>
        </div>

        {/* ── Notifications ── */}
        <div className="settings-card" style={card}>
          <div className="section-title">Notifications</div>

          {[
            { key: 'lowStock',    label: 'Low stock alerts' },
            { key: 'orderEmails', label: 'New order emails' },
            { key: 'reviewAlert', label: 'Customer review alerts' },
            { key: 'weeklyReport',label: 'Weekly report' },
          ].map(({ key, label }, i, arr) => (
            <div key={key} style={{ ...fieldStyle, borderBottom: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
              <span style={labelStyle}>{label}</span>
              <Switch
                checked={cfg.notifications[key]}
                onChange={v => set('notifications', key, v)}
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
                style={{ backgroundColor: cfg.notifications[key] ? '#534ab7' : undefined }}
              />
            </div>
          ))}
        </div>

        {/* ── Payments & Login ── */}
        {/* Merged from the old separate "Payment Methods" + "Login Options"
            cards — each was a single short toggle-row card that left a
            slab of empty space next to whatever taller card it happened to
            land beside. Both are "how a customer pays/signs in" concerns,
            so one card reads naturally and packs much tighter. */}
        <div className="settings-card" style={card}>
          <div className="section-title">Payments &amp; Login</div>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
            Cards, UPI, netbanking, and wallets are one bundled "Online Payment" option in the app — Razorpay's own
            checkout decides which of those to show. Turning this off disables all of them at once.
          </div>

          {[
            { key: 'online', label: 'Online Payment (Cards, UPI, Netbanking, Wallets via Razorpay)' },
            { key: 'cod',    label: 'Cash on Delivery' },
          ].map(({ key, label }) => (
            <div key={key} style={fieldStyle}>
              <span style={labelStyle}>{label}</span>
              <Switch
                checked={cfg.payments[key]}
                onChange={v => set('payments', key, v)}
                checkedChildren="Active"
                unCheckedChildren="Disabled"
                style={{ backgroundColor: cfg.payments[key] ? '#534ab7' : undefined }}
              />
            </div>
          ))}

          {[
            { key: 'otp', label: 'Login with OTP' },
          ].map(({ key, label }, i, arr) => (
            <div key={key} style={{ ...fieldStyle, borderBottom: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
              <span style={labelStyle}>{label}</span>
              <Switch
                checked={cfg.loginOptions[key]}
                onChange={v => set('loginOptions', key, v)}
                checkedChildren="Visible"
                unCheckedChildren="Hidden"
                style={{ backgroundColor: cfg.loginOptions[key] ? '#534ab7' : undefined }}
              />
            </div>
          ))}
        </div>

        {/* ── App Experience ── */}
        {/* Merged from the old separate "Mobile App Tabs" + "AI Virtual
            Try-On Simulator" cards — both are "what shows up for customers
            in the mobile app", same reasoning as above. */}
        <div className="settings-card" style={card}>
          <div className="section-title">App Experience</div>

          {[
            { key: 'contest',       label: 'Contest Tab' },
            { key: 'parenting',     label: 'Parenting (Guidance) Tab' },
            { key: 'subscriptions', label: 'Subscriptions (Settings)' },
          ].map(({ key, label }) => (
            <div key={key} style={fieldStyle}>
              <span style={labelStyle}>{label}</span>
              <Switch
                checked={cfg.mobileTabs[key]}
                onChange={v => set('mobileTabs', key, v)}
                checkedChildren="Visible"
                unCheckedChildren="Hidden"
                style={{ backgroundColor: cfg.mobileTabs[key] ? '#534ab7' : undefined }}
              />
            </div>
          ))}

          <div style={{ ...fieldStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>AI Virtual Try-On Simulator — let customers preview clothing on their child using AI</span>
            <Switch
              checked={cfg.features.tryOnEnabled}
              onChange={v => set('features', 'tryOnEnabled', v)}
              checkedChildren="On"
              unCheckedChildren="Off"
              style={{ backgroundColor: cfg.features.tryOnEnabled ? '#534ab7' : undefined }}
            />
          </div>
        </div>

        {/* ── Contact Support ── */}
        <div className="settings-card" style={card}>
          <div className="section-title">Contact Support</div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Support Email</span>
            <Input
              value={cfg.support.email}
              onChange={e => set('support', 'email', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Email Note</span>
            <Input
              value={cfg.support.emailNote}
              onChange={e => set('support', 'emailNote', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Support Phone</span>
            <Input
              value={cfg.support.phone}
              onChange={e => set('support', 'phone', e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div style={{ ...fieldStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Phone Note</span>
            <Input
              value={cfg.support.phoneNote}
              onChange={e => set('support', 'phoneNote', e.target.value)}
              style={{ width: 200 }}
            />
          </div>
        </div>

        {/* ── Mobile App Force Update ── */}
        <div className="settings-card" style={card}>
          <div className="section-title">Mobile App Force Update</div>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
            Android and iOS ship on their own schedules, so each has its own
            Minimum Version — set it to that platform's exact app.json
            version the moment you publish that build (e.g. "1.2.0"), then
            flip Force Update on. A platform blocks only its own users; a
            blank Minimum Version for a platform never blocks it. Force
            Update off (or nothing reachable at all) never blocks anyone.
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Force Update</span>
            <Switch
              checked={cfg.appUpdate.enabled}
              onChange={v => set('appUpdate', 'enabled', v)}
              checkedChildren="On"
              unCheckedChildren="Off"
              style={{ backgroundColor: cfg.appUpdate.enabled ? '#534ab7' : undefined }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Minimum Version — Android</span>
            <Input
              value={cfg.appUpdate.minVersionAndroid}
              onChange={e => set('appUpdate', 'minVersionAndroid', e.target.value)}
              placeholder="e.g. 1.2.0"
              style={{ width: 200 }}
            />
          </div>

          <div style={fieldStyle}>
            <span style={labelStyle}>Minimum Version — iOS</span>
            <Input
              value={cfg.appUpdate.minVersionIos}
              onChange={e => set('appUpdate', 'minVersionIos', e.target.value)}
              placeholder="e.g. 1.2.0"
              style={{ width: 200 }}
            />
          </div>

          <div style={{ ...fieldStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Update Message</span>
            <Input
              value={cfg.appUpdate.message}
              onChange={e => set('appUpdate', 'message', e.target.value)}
              placeholder="Optional — default message used if blank"
              style={{ width: 200 }}
            />
          </div>
        </div>

      </div>

      <CampaignManager />
    </div>
  );
}
